import {
  Button,
  Card,
  createColumnHelper,
  DEFAULT_TABLE_HEADER_ROW_HEIGHT,
  DEFAULT_TABLE_ROW_HEIGHT,
  EmptyState,
  Icon,
  Input,
  MessagePanel,
  Table,
  useCopyToClipboard,
} from '@dittolive/anvil'
import {
  CheckIcon,
  CopyIcon,
  EyeIcon,
  EyeSlashIcon,
  MagnifyingGlassIcon,
} from '@phosphor-icons/react'
import { graphql } from 'babel-plugin-relay/macro'
import AppContext from 'context'
import dayjs from 'dayjs'
import { useContext, useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'
import { usePortalMutation } from 'utils'

import { OfflineLicenseTokenManagerRequestLicenseMutation } from './__generated__/OfflineLicenseTokenManagerRequestLicenseMutation.graphql'
import OfflineLicenseTokenCreationModal from './OfflineLicenseTokenCreationModal'

const requestLicenseMutation = graphql`
  mutation OfflineLicenseTokenManagerRequestLicenseMutation($appId: ID!) {
    requestAppOfflineLicense(appId: $appId) {
      id
      licenses {
        id
        expiresAt
        token
      }
      offlineLicenseRequestStatus
    }
  }
`

export type License = {
  expiresAt: string | null | undefined
  id: string
  token: string
}

type Props = {
  appId: string
  appIsOfflineLicenseEligible: boolean
  licenses: License[]
  hasRequestedOfflineLicense?: boolean
}

export const MASKED_VALUE = Array(40).fill('*').join('')

const columnHelper = createColumnHelper<License>()

// The max height of the table is 5 rows + the header row. Everything beyond
// that will be scrollable so it doesn't grow too large.
const MAX_HEIGHT =
  DEFAULT_TABLE_HEADER_ROW_HEIGHT + DEFAULT_TABLE_ROW_HEIGHT * 5

function TokenCell({ license }: { license: License }) {
  const [isMasked, setIsMasked] = useState(true)
  const [doCopy, recentlyCopied, canCopy] = useCopyToClipboard(
    license.token || '',
    2000,
  )

  const token = useMemo(
    () => (isMasked ? MASKED_VALUE : license.token),
    [isMasked, license.token],
  )

  const VisibilityIcon = isMasked ? EyeIcon : EyeSlashIcon

  return (
    <div className="flex items-center justify-between gap-x-2">
      <p
        className="text-foreground-subtle flex-1 break-all font-mono"
        data-testid="tokenContainer"
      >
        {token}
      </p>

      <div className="flex items-center gap-x-1.5">
        <Button
          variant="ghost"
          size="icon"
          className="focus-visible:outline-focus-outline flex shrink-0 outline-none focus-visible:outline-2"
          data-testid="visibilityIcon"
          aria-label={isMasked ? 'Show token' : 'Hide token'}
          onClick={() => setIsMasked(!isMasked)}
        >
          <Icon svg={<VisibilityIcon />} className="text-foreground-subtle" />
        </Button>

        {canCopy && (
          <Button
            variant="ghost"
            size="icon"
            onClick={doCopy}
            className="focus-visible:outline-focus-outline flex shrink-0 outline-none focus-visible:outline-2"
            data-testid="copyButton"
            aria-label={recentlyCopied ? 'Token copied' : 'Copy token'}
          >
            {recentlyCopied ? (
              <Icon
                svg={<CheckIcon />}
                className="text-fill-success"
                data-testid="recentlyCopiedIcon"
              />
            ) : (
              <Icon
                svg={<CopyIcon />}
                className="text-foreground-subtle"
                data-testid="copyIcon"
              />
            )}
          </Button>
        )}
      </div>
    </div>
  )
}

/**
 * Component used to view and request offline license tokens for regular users, and to request new offline
 * license tokens for super admin users.
 */
const OfflineLicenseTokenManager = ({
  appId,
  appIsOfflineLicenseEligible,
  licenses,
  hasRequestedOfflineLicense,
}: Props) => {
  const { isSuperAdmin } = useContext(AppContext)

  const [error, setError] = useState<Error>()
  const [showModal, setShowModal] = useState(false)
  const [filter, setFilter] = useState('')

  const [requestOfflineLicense, isRequestingOfflineLicense] =
    usePortalMutation<OfflineLicenseTokenManagerRequestLicenseMutation>(
      requestLicenseMutation,
      {
        mutationName: 'requestAppOfflineLicense',
        sentryContext: { component: 'OfflineLicenseTokenManager' },
      },
    )

  const handleRequestLicense = () => {
    if (!isRequestingOfflineLicense && appIsOfflineLicenseEligible) {
      setError(undefined)
      requestOfflineLicense({
        variables: { appId },
        onError: setError,
      })
    }
  }

  const handleGenerateOfflineOnlyLicenseToken = () => {
    setShowModal(true)
  }

  const handleCloseModal = () => setShowModal(false)

  /**
   * Renders the primary action. Super admins may create the license, while
   * regular users may request the license.
   */
  const renderAction = () => {
    if (isSuperAdmin) {
      return (
        <Button
          variant="primary"
          onClick={handleGenerateOfflineOnlyLicenseToken}
        >
          New token
        </Button>
      )
    } else if (appIsOfflineLicenseEligible) {
      return (
        <Button
          variant="primary"
          disabled={isRequestingOfflineLicense || hasRequestedOfflineLicense}
          onClick={handleRequestLicense}
        >
          Request token
        </Button>
      )
    } else {
      return null
    }
  }

  const columns = useMemo(
    () => [
      columnHelper.accessor('token', {
        header: 'Token',
        cell: ({ row: { original } }) => <TokenCell license={original} />,
        enableSorting: false,
      }),
      columnHelper.accessor('expiresAt', {
        header: 'Expires',
        cell: ({ row: { original } }) => {
          const expiresAt = original.expiresAt
          const formatted = expiresAt ? dayjs(expiresAt) : null
          return (
            <span className="text-foreground-normal" data-testid="tokenExpiry">
              {formatted?.isValid() ? formatted.format('LLL') : '\u2014'}
            </span>
          )
        },
        enableSorting: false,
      }),
    ],
    [],
  )

  return (
    <>
      <OfflineLicenseTokenCreationModal
        appId={appId}
        show={showModal}
        onClose={handleCloseModal}
      />

      {!!error && (
        <MessagePanel
          variant="danger"
          className="mb-4"
          message={error.message}
        />
      )}

      {hasRequestedOfflineLicense && (
        <MessagePanel
          variant="info"
          className="mb-4"
          message={
            isSuperAdmin
              ? `An offline only license token has been requested for this database. To accept the request, click on the "New token" button`
              : 'Your offline only license request is pending. If accepted, your license will appear here once it has been created'
          }
        />
      )}

      <Card isDivided={false} className="shadow-none">
        <Card.Body isFlushed>
          <div className="w-full">
            <div className="flex h-12 items-center gap-2 px-2">
              <Input
                className="relative w-full"
                leadingIcon={
                  <Icon
                    className="text-foreground-subtle size-4"
                    svg={<MagnifyingGlassIcon />}
                  />
                }
                value={filter}
                onChange={(e) => setFilter(e.target.value)}
                placeholder="Search license tokens"
                disabled={licenses.length === 0 && !filter.length}
              />
              {renderAction()}
            </div>

            <AutoSizer disableHeight>
              {({ width }: { width: number }) => (
                <Table
                  columns={columns}
                  data={licenses}
                  width={width}
                  height={(rowCount: number) =>
                    rowCount > 0
                      ? Math.min(
                          MAX_HEIGHT,
                          DEFAULT_TABLE_HEADER_ROW_HEIGHT +
                            DEFAULT_TABLE_ROW_HEIGHT * rowCount,
                        )
                      : 156
                  }
                  activeOnHover={false}
                  tableCellClassName="first:pl-4 first:sm:pl-6"
                  tableHeaderCellClassName="first:pl-4 first:sm:pl-6"
                  emptyStateRenderer={() => (
                    <EmptyState
                      className="py-4"
                      message={
                        !!filter.length
                          ? 'No license tokens match the current filter'
                          : 'No offline only license tokens yet'
                      }
                    />
                  )}
                  options={{
                    state: {
                      globalFilter: filter,
                    },
                  }}
                />
              )}
            </AutoSizer>
          </div>
        </Card.Body>
      </Card>
    </>
  )
}

export default OfflineLicenseTokenManager
